import { Typography } from 'antd';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import { WithTextChange } from 'components/ui/Transitions';
import { TableTextController } from 'stores/common';
import { API_DATE_REGEX, formatDateByLocale } from 'utils/date';
import { toHumanReadableFormat } from 'utils/number/toHumanReadableFormat';
import { trimTrailingZeros } from 'utils/number/trimTrailingZeros';
import { translateApiValue } from 'utils/translateApiValue';

import { CHANGE_VALUE_TRANSITION_TIME } from '../common';
import { TableControllerProps } from '../types';

import s from './TextController.module.scss';

const TextController: React.FC<TableControllerProps<TableTextController>> = ({
  record,
  className,
}) => {
  const value = record.controllerValue;

  if (!value.trim()) {
    return null;
  }

  // Проверяем, является ли значение i18n ключом
  const isI18nKey = value.includes('.') && translateApiValue(value) !== value;

  if (isI18nKey) {
    // Если это ключ перевода, просто переводим
    const displayValue = translateApiValue(value);
    return (
      <WithTextChange
        className={className}
        label={displayValue}
        Wrapper={Typography.Text}
        transitionTime={CHANGE_VALUE_TRANSITION_TIME}
      />
    );
  }

  // Проверяем, является ли значение датой, и форматируем по локали
  const formattedDate = formatDateByLocale(value);

  if (formattedDate !== value || API_DATE_REGEX.test(value)) {
    return (
      <WithTextChange
        className={className}
        label={formattedDate}
        Wrapper={Typography.Text}
        transitionTime={CHANGE_VALUE_TRANSITION_TIME}
      />
    );
  }

  // Форматируем числовые строки, убирая конечные нули
  const trimmedValue = trimTrailingZeros(value);
  const parsedNumber = parseFloat(trimmedValue);

  // Если значение является числом, форматируем с разделителями разрядов
  const displayValue = !isNaN(parsedNumber)
    ? toHumanReadableFormat(parsedNumber)
    : trimmedValue;

  // Проверяем, является ли значение отрицательным числом
  const isNegative = !isNaN(parsedNumber) && parsedNumber < 0;

  return (
    <WithTextChange
      className={cn(className, {
        [s.negative]: isNegative,
      })}
      label={displayValue}
      Wrapper={Typography.Text}
      transitionTime={CHANGE_VALUE_TRANSITION_TIME}
    />
  );
};

export default observer(TextController);
